// Helpers for customer financials (lifetime value + receivables) shared by the
// Customer list, summary, and detail views. The server returns a map keyed by
// the invoice `customer_id` (cust_ID preferred, else DB id) — `customerFinanceKey`
// must mirror that exact key.

const CURRENCY_SYMBOLS = { INR: "₹", USD: "$", EUR: "€" };

export function currencySymbol(currency) {
  return CURRENCY_SYMBOLS[String(currency || "INR").toUpperCase()] || "₹";
}

export function formatCurrency(amount, currency = "INR") {
  const n = Number(amount);
  const safe = Number.isFinite(n) ? n : 0;
  const code = String(currency || "INR").toUpperCase();
  const locale = code === "INR" ? "en-IN" : "en-US";
  return `${currencySymbol(code)}${safe.toLocaleString(locale, {
    maximumFractionDigits: 2,
  })}`;
}

/** Compact form for KPI cards: ₹1.2L / ₹3.4Cr (INR) or ₹12.3K. */
export function formatCurrencyCompact(amount, currency = "INR") {
  const n = Number(amount);
  const safe = Number.isFinite(n) ? n : 0;
  const sym = currencySymbol(currency);
  const abs = Math.abs(safe);
  const sign = safe < 0 ? "-" : "";

  if (String(currency || "INR").toUpperCase() === "INR") {
    if (abs >= 1e7) return `${sign}${sym}${(abs / 1e7).toFixed(2)}Cr`;
    if (abs >= 1e5) return `${sign}${sym}${(abs / 1e5).toFixed(2)}L`;
    if (abs >= 1e3) return `${sign}${sym}${(abs / 1e3).toFixed(1)}K`;
    return formatCurrency(safe, currency);
  }

  if (abs >= 1e9) return `${sign}${sym}${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${sign}${sym}${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}${sym}${(abs / 1e3).toFixed(1)}K`;
  return formatCurrency(safe, currency);
}

/** Mirrors the Admin invoice form key: cust_ID preferred, else DB id. */
export function customerFinanceKey(customer) {
  if (!customer) return "";
  const cid = customer.cust_ID != null ? String(customer.cust_ID).trim() : "";
  if (cid) return cid;
  return customer.id != null ? String(customer.id) : "";
}

export const EMPTY_FINANCE = Object.freeze({
  invoicedTotal: 0,
  outstanding: 0,
  collected: 0,
  overdueAmount: 0,
  overdueCount: 0,
  invoiceCount: 0,
  lastInvoiceDate: null,
});

export function getCustomerFinance(financeMap, customer) {
  if (!financeMap || !customer) return EMPTY_FINANCE;
  return financeMap[customerFinanceKey(customer)] || EMPTY_FINANCE;
}

/** Aggregate receivables across a list of customers using the finance map. */
export function aggregateReceivables(customers = [], financeMap = {}) {
  const totals = {
    invoicedTotal: 0,
    collected: 0,
    outstanding: 0,
    overdueAmount: 0,
    customersWithDues: 0,
    customersWithInvoices: 0,
  };

  for (const customer of Array.isArray(customers) ? customers : []) {
    const f = getCustomerFinance(financeMap, customer);
    totals.invoicedTotal += f.invoicedTotal || 0;
    totals.collected += f.collected || 0;
    totals.outstanding += f.outstanding || 0;
    totals.overdueAmount += f.overdueAmount || 0;
    if ((f.outstanding || 0) > 0) totals.customersWithDues += 1;
    if ((f.invoiceCount || 0) > 0) totals.customersWithInvoices += 1;
  }

  return totals;
}
